import { useEffect, useMemo, useState } from "react";
import { api, formatDate } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", date: "", start_time: "09:00", end_time: "10:00" });

  const load = () => api.get("/events").then(r => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const daysCount = daysInMonth(year, month);
  const startDay = new Date(year, month, 1).getDay();

  const cells = useMemo(() => {
    const arr = [];
    for (let i = 0; i < startDay; i++) arr.push(null);
    for (let d = 1; d <= daysCount; d++) arr.push(d);
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [startDay, daysCount]);

  const eventsFor = (day) => {
    if (!day) return [];
    const iso = new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
    return events.filter(e => e.date === iso);
  };

  const create = async () => {
    if (!form.title || !form.date) return toast.error("Title and date required");
    await api.post("/events", form);
    toast.success("Event added");
    setOpen(false);
    setForm({ title: "", date: "", start_time: "09:00", end_time: "10:00" });
    load();
  };

  const monthName = cursor.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Calendar"
        subtitle="Shared team schedule and appointments"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="add-event-btn">
                <Plus className="w-4 h-4 mr-1" /> New Event
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New Event</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title-input" /></div>
                <div><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Start</Label><Input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} /></div>
                  <div><Label>End</Label><Input type="time" value={form.end_time} onChange={e => setForm({...form, end_time: e.target.value})} /></div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={create} className="bg-brand-aqua hover:bg-brand-aqua-hover text-white" data-testid="save-event-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="kpi-card">
        <div className="flex items-center justify-between mb-4">
          <div className="font-heading font-semibold text-xl text-slate-900">{monthName}</div>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month - 1, 1))} data-testid="prev-month-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
            <Button variant="outline" size="icon" onClick={() => setCursor(new Date(year, month + 1, 1))} data-testid="next-month-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-px bg-slate-200 rounded-md overflow-hidden">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
            <div key={d} className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-medium py-2 text-center">{d}</div>
          ))}
          {cells.map((day, i) => {
            const evs = eventsFor(day);
            const today = new Date();
            const isToday = day && today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
            return (
              <div key={`${year}-${month}-${day ?? `blank-${i}`}`} className={`bg-white min-h-[96px] p-2 ${!day ? "bg-slate-50" : ""}`}>
                {day && (
                  <>
                    <div className={`text-xs font-medium num ${isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-aqua text-white" : "text-slate-600"}`}>{day}</div>
                    <div className="mt-1 space-y-1">
                      {evs.map(e => (
                        <div key={e.id} className="text-[11px] px-1.5 py-0.5 rounded bg-brand-aqua-light text-brand-aqua-hover truncate">
                          {e.start_time} {e.title}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
